// SMS composition helpers (FE mirror of the eventsmagic-api boundary `messageChannels`).
//
// A host writes a short token like {{inviteUrl}} (~12 chars) but the SENT SMS carries a full URL, so
// the character count and segment estimate must account for that expansion. These helpers drive the
// compose-page counter + the "add the link" guard; the server re-checks the same rules on send.

export const SMS_MAX_CHARS = 240;

// The per-guest link token an SMS body must carry, by message mode.
export const SMS_LINK_TOKENS_INVITE = ["inviteUrl", "invite_url"];
export const SMS_LINK_TOKENS_EVENT = ["eventUrl", "event_url"];

const LINK_ESTIMATE = 60; // https://app.eventsmagic.com/e/<slug>?inv=<token> ≈ 60–90; a safe midpoint
const TOKEN_ESTIMATE = 14; // a typical name/group/email substitution width

/** Estimate the DELIVERED length of a raw SMS body (link tokens → URL width, other {{tokens}} → a value width). */
export const estimateSmsLength = (raw: string | null | undefined): number => {
  let body = String(raw ?? "");
  for (const t of [...SMS_LINK_TOKENS_INVITE, ...SMS_LINK_TOKENS_EVENT]) {
    body = body.split(`{{${t}}}`).join("#".repeat(LINK_ESTIMATE));
  }
  body = body.replace(/\{\{\s*[\w.]+\s*\}\}/g, "#".repeat(TOKEN_ESTIMATE));
  return body.length;
};

/** Does the body contain at least one of the given link tokens (the channel's required link)? */
export const bodyHasLinkToken = (raw: string | null | undefined, tokens: string[]): boolean => {
  const body = String(raw ?? "");
  return tokens.some((t) => body.includes(`{{${t}}}`));
};

/**
 * The {{tokens}} that appear MORE THAN ONCE in the body (normalized, whitespace-insensitive). A repeated
 * variable in a text message — especially the link — wastes segments and is almost always a mistake.
 */
export const duplicateTokens = (raw: string | null | undefined): string[] => {
  const counts = new Map<string, number>();
  for (const m of String(raw ?? "").match(/\{\{\s*[\w.]+\s*\}\}/g) || []) {
    const token = m.replace(/\s+/g, "");
    counts.set(token, (counts.get(token) || 0) + 1);
  }
  return [...counts.entries()].filter(([, n]) => n > 1).map(([t]) => t);
};

// GSM-7 basic character set (3GPP 03.38, approx). Anything outside it (emoji, curly quotes, many
// accents) forces UCS-2 encoding, which halves the per-segment capacity.
const GSM7 = new Set(
  (
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡" +
    "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà" +
    "^{}\\[~]|€"
  ).split(""),
);

type SmsEncoding = "gsm" | "ucs2";

/** GSM-7 if every character (ignoring {{tokens}}, which resolve to ASCII/URLs) is in the basic set. */
export const smsEncoding = (raw: string | null | undefined): SmsEncoding => {
  const body = String(raw ?? "").replace(/\{\{\s*[\w.]+\s*\}\}/g, "");
  for (const ch of body) {
    if (!GSM7.has(ch)) {
      return "ucs2";
    }
  }
  return "gsm";
};

/** Number of SMS segments the (link-expanded) body would take, given its encoding. 0 for an empty body. */
export const smsSegments = (raw: string | null | undefined): number => {
  const len = estimateSmsLength(raw);
  if (len === 0) {
    return 0;
  }
  const gsm = smsEncoding(raw) === "gsm";
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  return len <= single ? 1 : Math.ceil(len / multi);
};

// #75 — an invitation's text message carries the guest's personal link, so the server ends it with this line (mirror
// of the API's `PERSONAL_LINK_NOTICE_SMS`). The counter measures the message WITH it, because that is what is sent
// and what the server's length cap counts.
export const SMS_PERSONAL_LINK_NOTICE = "This link is personal - please don't share it.";

/** The text message as it goes out when it carries the guest's personal link (an invitation). */
export const withPersonalLinkNoticeSms = (raw: string | null | undefined): string => {
  const text = String(raw ?? "").trimEnd();

  if (!text.trim() || text.endsWith(SMS_PERSONAL_LINK_NOTICE)) {
    return text;
  }

  return `${text}\n${SMS_PERSONAL_LINK_NOTICE}`;
};
